import axios from 'axios';
import socket from './socket';
import { API_BASE_URL } from '../config';

// Create API instance with base URL
const marketApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Get all available stocks
 * @returns {Promise<Array>} - List of stocks
 */
export const getStocks = async () => {
  try {
    const response = await marketApi.get('/api/stocks');
    return response.data;
  } catch (error) {
    console.error('Error fetching stocks:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to fetch market data. Please refresh.';
    throw new Error(errorMessage);
  }
};

// Store callbacks from components
const stockUpdateSubscribers = new Set();
const marketEventSubscribers = new Set();

let listenersAttached = false;
let attachedToSocket = null; // Track which socket instance we attached to

function ensureSocketListeners() {
  // Get the actual socket instance to check if it changed (e.g. reconnect with new token)
  const currentSocket = socket.get ? socket.get() : null;

  // Re-attach if socket instance changed or listeners were never attached
  if (currentSocket && (currentSocket !== attachedToSocket || !listenersAttached)) {
    console.log('[market.js] Attaching socket listeners...');

    // If we were attached to an old socket, remove old listeners
    if (attachedToSocket && attachedToSocket !== currentSocket) {
      try {
        attachedToSocket.off('stock-updates');
        attachedToSocket.off('market-event');
      } catch (e) { /* old socket may be gone */ }
    }

    currentSocket.on('stock-updates', (updates) => {
      stockUpdateSubscribers.forEach(callback => {
        try {
          callback(updates);
        } catch (e) {
          console.error('[market.js] Error in stockUpdateCallback:', e);
        }
      });
    });

    currentSocket.on('market-event', (event) => {
      marketEventSubscribers.forEach(callback => {
        try {
          callback(event);
        } catch (e) {
          console.error('[market.js] Error in marketEventCallback:', e);
        }
      });
    });

    attachedToSocket = currentSocket;
    listenersAttached = true;
  } else if (!currentSocket) {
    // Socket not ready yet — the pending listener queue in socket.js will handle it
    // Register via the wrapper so they get queued
    if (!listenersAttached) {
      socket.on('stock-updates', (updates) => {
        stockUpdateSubscribers.forEach(callback => {
          try {
            callback(updates);
          } catch (e) {
            console.error('[market.js] Error in stockUpdateCallback:', e);
          }
        });
      });

      socket.on('market-event', (event) => {
        marketEventSubscribers.forEach(callback => {
          try {
            callback(event);
          } catch (e) {
            console.error('[market.js] Error in marketEventCallback:', e);
          }
        });
      });

      listenersAttached = true;
    }
  }
}

// Call it once when the module loads — if socket isn't ready yet, listeners are queued
ensureSocketListeners();

/**
 * Get real-time market data via socket.io
 * This function sets up listeners for stock updates
 * @param {Function} onStockUpdate - Callback for stock updates
 * @param {Function} onMarketEvent - Callback for market events
 * @returns {Function} - Cleanup function to remove listeners
 */
export const subscribeToMarketData = (onStockUpdate, onMarketEvent) => {
  // Always try to ensure listeners are attached when a component subscribes.
  // This helps if the socket was initialized after market.js was first imported.
  ensureSocketListeners();

  if (onStockUpdate && typeof onStockUpdate === 'function') {
    stockUpdateSubscribers.add(onStockUpdate);
  }
  if (onMarketEvent && typeof onMarketEvent === 'function') {
    marketEventSubscribers.add(onMarketEvent);
  }

  // Initial data comes from REST API (/api/stocks) in fetchInitialStocks.
  // Live updates come from socket 'stock-updates' broadcasts (every 10s).

  return () => {
    if (onStockUpdate) {
      stockUpdateSubscribers.delete(onStockUpdate);
    }
    if (onMarketEvent) {
      marketEventSubscribers.delete(onMarketEvent);
    }
  };
};

// If your socket.js has a reliable way to notify when the socket is truly ready (connected),
// calling ensureSocketListeners() from that notification point would be most robust.
// For example, if socket.js used an event emitter:
// import { socketEmitter } from './socket'; // Hypothetical
// socketEmitter.on('socketReady', ensureSocketListeners);

/**
 * Get historical price data for a specific stock
 * @param {string} stockId - Stock ID to get history for
 * @param {string} period - Time period ('1h', '1d', '1w', '1m')
 * @returns {Promise<Array>} - Array of price points
 */
export const getStockHistory = async (stockId, period = '1d') => {
  try {
    const response = await marketApi.get(`/api/stocks/${stockId}/history`, {
      params: { period }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock history:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to fetch stock history. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get recent market events
 * @param {number} limit - Maximum number of events to return
 * @returns {Promise<Array>} - Array of recent market events
 */
export const getMarketEvents = async (limit = 10) => {
  try {
    const response = await marketApi.get('/api/events', {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching market events:', error);
    
    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to fetch market events. Please try again.';
    throw new Error(errorMessage);
  }
};